import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { productCategories } from '@/mocks/products';

function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || productCategories[0].id;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const activeCategory = productCategories.find((c) => c.id === activeTab) || productCategories[0];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="產品系列 | 性福寶——找到適合您的活力方案"
        description="探索性福寶全系列產品：經典系列、尊享系列、伴侶系列、日常養護系列。針對不同需求設計，天然精華配方，幫助提升活力與自信。"
        keywords="性福寶產品,性福寶系列,性福寶正品購買,性福寶官方價格"
        canonicalUrl="https://xingfubao.com/category"
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首頁', href: '/' },
              { label: '產品系列' },
            ]}
          />
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                產品系列
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                針對不同需求設計多個系列，天然草本精華，找到最適合您的活力方案
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
              {productCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeTab === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mb-10">
              <div className="relative rounded-lg overflow-hidden h-48 md:h-64 mb-8">
                <img
                  src={activeCategory.image}
                  alt={activeCategory.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent flex items-end p-6 md:p-8">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">{activeCategory.name}</h2>
                    <p className="text-sm text-background-200">{activeCategory.description}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {activeCategory.products.map((product) => (
                <div
                  key={product.id}
                  className="bg-background-100/70 rounded-lg overflow-hidden group cursor-default"
                >
                  <div className="p-6">
                    {product.badge && (
                      <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-2.5 py-1 rounded-full mb-3">
                        {product.badge}
                      </span>
                    )}
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{product.name}</h3>
                    <p className="text-sm text-foreground-600 mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="font-heading text-2xl font-bold text-primary-600">¥{product.price}</span>
                      {product.originalPrice && (
                        <span className="text-sm text-foreground-400 line-through">¥{product.originalPrice}</span>
                      )}
                    </div>
                    <button className="w-full bg-primary-500 text-background-50 py-2.5 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                      立即購買
                    </button>
                    <p className="text-xs text-foreground-400 text-center mt-2">
                      庫存: {product.stock} 件
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">不確定選哪個系列？</h3>
            <p className="text-sm text-foreground-600 mb-6">我們的健康顧問團隊隨時為您提供專業建議</p>
            <Link
              to="/about#contact"
              className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              聯繫顧問
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default CategoryPage;